<script setup lang="ts">
import { MAIN_LOGO_ASSET, CONNECTED_US_ITEMS } from '~/domains/career/main/components/career-main-connectedUs/career-main-connectedUs.consts'

const { t } = useI18n()
const { isMobile } = useBSBreakpoints()
</script>

<template>
  <div class="career-main-connectedUs">
    <div class="career-main-connectedUs__container">
      <div class="career-main-connectedUs__hero">
        <div class="career-main-connectedUs__hero-content">
          <UiHeader
            :header="$t('career.connectedUs.title') "
            :position="isMobile ? 'center' : 'left'"
            header-size="h5"
            header-size-tablet="h5"
            header-size-mobile="h5"
          />

          <p class="career-main-connectedUs__description">
            {{ t('career.connectedUs.description') }}

            <span class="career-main-connectedUs__description-second">
              {{ t('career.connectedUs.descriptionSecond') }}
            </span>

            {{ t('career.connectedUs.descriptionThird') }}
          </p>

          <a
            href="https://youtube.com/watch?v=example"
            target="_blank"
            class="career-main-connectedUs__video-link"
          >
            {{ t('career.connectedUs.watchVideo') }}

            <img
              :src="'/icons/play_icon_black.svg'"
              class="career-main-connectedUs__video-icon"
            >
          </a>
        </div>

        <div class="career-main-connectedUs__hero-image">
          <picture>
            <source
              media="(min-width: 1200px)"
              :srcset="MAIN_LOGO_ASSET.srcDesktop"
            >

            <img
              class="career-main-connectedUs__hero-image-img"
              :srcset="MAIN_LOGO_ASSET.srcMobile"
              :alt="t(MAIN_LOGO_ASSET.alt)"
            >
          </picture>
        </div>
      </div>

      <div class="career-main-connectedUs__items">
        <div
          v-for="item in CONNECTED_US_ITEMS"
          :key="item.id"
          class="career-main-connectedUs__items-item"
        >
          <img
            :src="t(item.image.src)"
            :alt="t(item.image.alt)"
            class="career-main-connectedUs__items-item-image"
          >

          <p class="career-main-connectedUs__items-item-description">
            {{ t(item.description) }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.career-main-connectedUs {
  margin: 4.8rem 0;
  padding: 0 1.6rem;

  &__container {
    display: flex;
    flex-direction: column;
    align-items: center;
    align-self: stretch;
    gap: 3.2rem;
    padding: 6.4rem 1.6rem;
    background-color: $gray-background;
    border-radius: 2.4rem;
  }

  &__hero {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    gap: 4.8rem;

    &-content {
      display: flex;
      flex-direction: column;
      gap: 2.4rem;
    }

    &-image {
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  &__description {
    @include body-3-medium;
    color: $text-400;
    text-align: center;

    &-second {
      font-weight: 600;
    }
  }

  &__video-link {
    @include body-4-medium;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.4rem;
    width: max-content;
    margin: 0 auto;
    padding-bottom: 2px;
    color: $text-400;
    text-align: center;
    text-decoration: none;
    border-bottom: 1px solid $purple-500;
    visibility: hidden;
  }

  &__video-icon {
    width: 1.5rem;
    height: 1.5rem;
    padding-bottom: 0.15rem;
  }

  &__items {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    gap: 0.5rem;
    width: 100%;

    :nth-child(1) {
      border-radius: 2.4rem 2.4rem 0 0;
    }

    :nth-last-child(1) {
      border-radius: 0 0 2.4rem 2.4rem;
    }

    &-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1.6rem;
      width: 100%;
      padding: 3.2rem;
      background-color: $white;

      &-description {
        @include body-4;
        color: $text-500;
      }

      &-image {
        max-width: 20rem;
        max-height: 4rem;
      }
    }
  }

  @include atXS {
    &__container {
      padding: 6.4rem 3.2rem;
    }

    &__title, &__description {
      text-align: left;
    }

    &__video-link {
      margin: 0;
    }

    &__hero-image {
      padding-bottom: 2.4rem;
    }

    &__items {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 0.5rem;
    }
  }

  @include atM {
    margin: 4.8rem auto 10rem;
    padding: 0 6.4rem;

    &__container {
      max-width: 136rem;
      margin: 0 auto;
    }

    &__hero {
      flex-direction: row;
      padding-bottom: 2.4rem;

      &-content, &-image {
        flex-basis: 50%;
        padding-bottom: 0;
      }
    }

    &__title {
      @include h5;
    }

    &__description, &__video-link {
      @include body-1;
    }

    &__video-link {
      gap: 1rem;
    }

    &__video-icon {
      padding-bottom: 0;
    }

    &__items {
      display: flex;
      flex-direction: row;
      align-items: stretch;
      gap: 0;

      :nth-child(1) {
        border-radius: 2.4rem 0 0 2.4rem;
      }

      :nth-last-child(1) {
        border-radius: 0 2.4rem 2.4rem 0;
      }

      &-item {
        flex: 1 1 0;
        max-height: 23rem;
        transition: transform 200ms ease, box-shadow 200ms ease;

        &:hover {
          box-shadow: 0 10px 30px rgba(124, 73, 190, 0.1);
          transform: translateY(-5px);
        }

        &-description {
          @include body-3;
          text-align: center;
        }
      }
    }
  }
}
</style>

